<template>
  <div class="policy-notes" dir="rtl">
    <button class="policy-toggle" :class="{ 'is-open': is_open }" :title="title" @click="toggle">
      <span class="toggle-caret">{{ is_open ? '▾' : '◂' }}</span>
      <span>{{ title }}</span>
      <span v-if="is_loaded && !has_error" class="toggle-count">{{ mainRules.length }}</span>
    </button>

    <div v-if="is_open" class="policy-panel">
      <p v-if="is_loading" class="policy-note">...טוען כללים</p>

      <p v-else-if="has_error" class="policy-error">
        שגיאה בטעינת כללי המדיניות
        <button class="link-btn" @click="load">נסי שוב</button>
      </p>

      <template v-else>
        <div v-for="section in sections" :key="section.key" class="policy-section">
          <button
            v-if="section.collapsible"
            class="section-toggle"
            @click="toggleSection(section.key)"
          >
            <span class="toggle-caret">{{ isSectionOpen(section) ? '▾' : '◂' }}</span>
            <span>{{ section.title }}</span>
            <span class="toggle-count">{{ section.rules.length }}</span>
          </button>
          <h4 v-else-if="section.title" class="section-title">{{ section.title }}</h4>

          <template v-if="isSectionOpen(section)">
            <ul v-if="section.rules.length" class="policy-list">
              <li v-for="rule in section.rules" :key="rule.id" class="policy-item">
                <div class="item-instruction">{{ rule.instruction }}</div>
                <div v-if="rule.reason" class="item-reason">{{ rule.reason }}</div>
                <div class="item-badges">
                  <span v-if="rule.requires_human" class="badge badge-human">דורש בדיקת מגייסת</span>
                  <span v-if="evidenceCount(rule)" class="badge badge-evidence">{{ evidenceLabel(rule) }}</span>
                </div>
              </li>
            </ul>
            <p v-else class="policy-empty">אין כללים</p>
          </template>
        </div>

        <router-link class="full-page-link" :to="fullPageRoute">פתח מסך מלא</router-link>
      </template>
    </div>
  </div>
</template>
<script>
// Read-only peek at "מדיניות שליחה" rules, for places that already show a
// company or a position (company info panel, position modal). Editing stays on
// the dedicated /sending-policies page, which the footer link opens.
export default {
  name: 'sending-policy-notes',
  props: {
    companyId: {
      type: [Number, String],
      required: true
    },
    // When given, the panel leads with this position's own rules and pushes the
    // company-wide rules into a collapsed "כללי חברה" block.
    positionId: {
      type: [Number, String],
      default: null
    },
    startOpen: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      is_open: this.startOpen,
      is_loading: false,
      is_loaded: false,
      has_error: false,
      company_rules: [],
      position_rules: [],
      // Section keys the recruiter explicitly opened (company block starts closed).
      open_sections: {}
    }
  },
  computed: {
    title () {
      return this.positionId ? 'הערות מדיניות למשרה זו' : 'מדיניות שליחה'
    },
    // The rules the toggle counts — the position's own in position mode.
    mainRules () {
      return this.positionId ? this.position_rules : this.company_rules
    },
    sections () {
      if (!this.positionId) {
        return [
          { key: 'company', title: '', rules: this.company_rules, collapsible: false }
        ]
      }
      return [
        { key: 'position', title: 'כללים למשרה זו', rules: this.position_rules, collapsible: false },
        { key: 'company', title: 'כללי חברה', rules: this.company_rules, collapsible: true }
      ]
    },
    fullPageRoute () {
      return { name: 'sending-policies', query: { company_id: String(this.companyId) } }
    }
  },
  mounted () {
    if (this.is_open) {
      this.load()
    }
  },
  methods: {
    toggle () {
      this.is_open = !this.is_open
      if (this.is_open && !this.is_loaded && !this.is_loading) {
        this.load()
      }
    },
    load () {
      if (this.is_loading || !this.companyId) {
        return
      }
      this.is_loading = true
      this.has_error = false
      return this.$network_utils
        .getSendingPolicies({ company_id: this.companyId })
        .then(res => {
          this.company_rules = this.activeOnly((res && res.company_rules) || [])
          this.position_rules = this.activeOnly(this.rulesForPosition(res))
          this.is_loaded = true
        })
        .catch(() => {
          this.has_error = true
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    rulesForPosition (res) {
      if (!this.positionId) {
        return []
      }
      const groups = (res && res.position_rules) || []
      const group = groups.find(g => String(g.position_id) === String(this.positionId))
      return (group && group.rules) || []
    },
    // Disabled rules are history — this view is about what applies right now.
    activeOnly (rules) {
      return rules.filter(rule => rule.status === 'active')
    },
    evidenceCount (rule) {
      return Array.isArray(rule.evidence) ? rule.evidence.length : 0
    },
    evidenceLabel (rule) {
      const count = this.evidenceCount(rule)
      return count === 1 ? 'ראיה אחת' : count + ' ראיות'
    },
    isSectionOpen (section) {
      return !section.collapsible || !!this.open_sections[section.key]
    },
    toggleSection (key) {
      this.$set(this.open_sections, key, !this.open_sections[key])
    }
  }
}
</script>
<style scoped>
.policy-notes {
  text-align: right;
}
.policy-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f5f6fe;
  border: 1px solid #c7cbf5;
  border-radius: 14px;
  color: #3b3f99;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  padding: 4px 12px;
  cursor: pointer;
}
.policy-toggle:hover {
  background: #e2e3ff;
}
.toggle-caret {
  font-size: 11px;
  color: #6b70c9;
}
.toggle-count {
  background: #fff;
  border: 1px solid #c7cbf5;
  border-radius: 10px;
  font-size: 11px;
  padding: 0 7px;
}
.policy-panel {
  margin-top: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  max-height: 320px;
  overflow-y: auto;
}
.policy-section {
  margin-bottom: 10px;
}
.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 6px;
}
.section-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  padding: 0 0 6px;
  cursor: pointer;
}
.policy-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.policy-item {
  border-right: 3px solid #c7cbf5;
  background: #f8fafc;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 6px;
}
.item-instruction {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;
}
.item-reason {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.item-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.badge {
  display: inline-block;
  border-radius: 12px;
  padding: 1px 9px;
  font-size: 11px;
  font-weight: 700;
}
.badge-human {
  background: #fdecea;
  color: #c0392b;
}
.badge-evidence {
  background: #f1f5f9;
  color: #475569;
}
.policy-empty,
.policy-note {
  font-size: 12px;
  color: #94a3b8;
  padding: 2px 0 4px;
}
.policy-error {
  font-size: 12px;
  color: #c0392b;
}
.link-btn {
  background: none;
  border: none;
  color: #3273dc;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  padding: 0 4px;
  text-decoration: underline;
}
.full-page-link {
  display: inline-block;
  margin-top: 4px;
  color: #3273dc;
  font-size: 12px;
  text-decoration: underline;
}
</style>
